/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Form, Row, Col, Card, Space, Table, Modal, Divider,
  Input, InputNumber, Select, Button, DatePicker, Typography, message, Flex,
} from "antd";
import { SaveOutlined, ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { detailColumns, deliveryColumn } from "./model";
import PackingListService from "../../../service/ERP/PackingList.service";
import "./MyPage.css";
import ActionGroup, { PrintButton } from "../../../components/action-group/ActionGroup";

const pkService = PackingListService();
const { TextArea } = Input;

const LOADING_UNIT_OPTIONS = [
  { value: "PALLETS", label: "PALLETS" },
  { value: "CARTONS", label: "CARTONS" },
];

const dv = (v) => (v ? dayjs(v) : null);
const fmt = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

const MyManage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || {};
  const isCreate = config?.action === "create";
  /* โอหมดดูอย่างเดียว — ปิดการแก้ไขทั้งฟอร์ม ส่อนปุ่มบันทึก/ลบแถว */
  const isView = config?.action === "view";

  const [form] = Form.useForm();
  const [detail, setDetail] = useState([]);
  /* ใบนี้มีทั้งสินค้าที่บรรจุเป็นกล่องและเป็นพาเลตปนกันหรือไม่
     ใช้เตือนผู้ใช้ เพราะใบ Packing List ระบุหน่วยการโหลดได้หน่วยเดียว */
  const [packTypeMixed, setPackTypeMixed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* modal เลือกใบส่งมอบ */
  const [openDelModal, setOpenDelModal] = useState(false);
  const [delList, setDelList] = useState([]);
  const [delLoading, setDelLoading] = useState(false);
  const [delKeyword, setDelKeyword] = useState("");
  const [showAllDel, setShowAllDel] = useState(false);

  /* ---- แถวรายการสินค้า ---- */
  const deleteDetail = (seq) => {
    setDetail((prev) =>
      prev.filter((d) => d.seq !== seq).map((d, i) => ({ ...d, seq: i + 1 }))
    );
  };

  const updateDetail = (index, field, value) => {
    setDetail((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  /* ---- โหลดรายการใบส่งมอบให้เลือก ---- */
  const loadDeliveryList = async (keyword = delKeyword, all = showAllDel) => {
    try {
      setDelLoading(true);
      const res = await pkService.availableDelivery({ keyword, all });
      setDelList(res.data?.data || []);
    } catch {
      message.error("โหลดรายการใบส่งมอบไม่สำเร็จ");
    } finally {
      setDelLoading(false);
    }
  };

  const openDeliveryPicker = () => {
    setOpenDelModal(true);
    loadDeliveryList();
  };

  /* ---- เลือกใบส่งมอบ → prefill ---- */
  /* เติมฟอร์มจากข้อมูลตั้งต้น (ใช้ร่วมกันทั้งดึงจากใบส่งมอบ และดึงจาก Job Order) */
  const applyPrefill = (header, det) => {

      form.setFieldsValue({
        delcode: header.delcode,
        jocode: header.jocode,
        cuscode: header.cuscode,
        cusname: header.cusname,
        brand: header.brand,
        pk_date: dayjs(),
        consignee_name: header.consignee_name,
        consignee_address: header.consignee_address,
        eori_no: header.eori_no,
        notify_party: header.notify_party || "SAME AS ABOVE",
        customer_po: header.customer_po,
        country_of_origin: header.country_of_origin || "THAILAND",
        etd: dv(header.etd),
        eta: dv(header.eta),
        loading_qty: header.loading_qty,
        /* หน่วยการโหลดคำนวณมาจาก pack_type ของสินค้าจริงในใบส่งมอบแล้ว */
        loading_unit: header.loading_unit || "CARTONS",
        net_weight: header.net_weight,
        gross_weight: header.gross_weight,
      });

      setPackTypeMixed(Number(header.pack_type_mixed) === 1);
      setDetail(
        (det || []).map((d) => ({
          ...d,
          production_date: dv(d.production_date),
          expiry_date: dv(d.expiry_date),
        }))
      );
  };

  const handleSelectDelivery = async (record) => {
    try {
      setLoading(true);
      const res = await pkService.prefillFromDelivery(record.delcode);
      const { header, detail: det } = res.data?.data || {};
      if (!header) {
        message.warning("ไม่พบข้อมูลใบส่งมอบ");
        return;
      }
      applyPrefill(header, det);
      setOpenDelModal(false);
      message.success(`ดึงข้อมูลจาก ${header.delcode} แล้ว`);
    } catch (err) {
      message.error(err?.response?.data?.message || "ดึงข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };


  /* ---- โหลดข้อมูลตอนแก้ไข ---- */
  const loadData = async (code) => {
    try {
      setLoading(true);
      const res = await pkService.get(code);
      const { header, detail: det } = res.data?.data || {};
      if (!header) return;

      form.setFieldsValue({
        ...header,
        pk_date: dv(header.pk_date),
        inv_date: dv(header.inv_date),
        etd: dv(header.etd),
        eta: dv(header.eta),
      });

      setDetail(
        (det || []).map((d) => ({
          ...d,
          production_date: dv(d.production_date),
          expiry_date: dv(d.expiry_date),
        }))
      );
    } catch {
      message.error("โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isCreate && config?.code) {
      loadData(config.code);
    } else if (isCreate) {
      form.setFieldsValue({
        pk_date: dayjs(),
        notify_party: "SAME AS ABOVE",
        country_of_origin: "THAILAND",
        loading_unit: "PALLETS",
      });
      /* เข้ามาจากปุ่ม "สร้าง Packing List" ในหน้า Delivery Note → ดึงข้อมูลให้เลย */
      if (config?.delcode) {
        handleSelectDelivery({ delcode: config.delcode });
      }
    }
  }, []);

  /* ---- รวมน้ำหนักจากรายการสินค้า ---- */
  const totals = detail.reduce(
    (acc, d) => ({
      cases: acc.cases + Number(d.quantity_case || 0),
      net: acc.net + Number(d.net_weight || 0),
      gross: acc.gross + Number(d.gross_weight || 0),
    }),
    { cases: 0, net: 0, gross: 0 }
  );

  const applyTotals = () => {
    form.setFieldsValue({
      net_weight: Number(totals.net.toFixed(2)),
      gross_weight: Number(totals.gross.toFixed(2)),
    });
    message.success("อัปเดตน้ำหนักรวมจากรายการสินค้าแล้ว");
  };

  /* ---- บันทึก ---- */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (detail.length === 0) {
        message.warning("กรุณาเลือกใบส่งมอบเพื่อดึงรายการสินค้า");
        return;
      }

      setSaving(true);
      const payload = {
        header: {
          ...values,
          pk_date: fmt(values.pk_date),
          inv_date: fmt(values.inv_date),
          etd: fmt(values.etd),
          eta: fmt(values.eta),
        },
        detail: detail.map((d) => ({
          ...d,
          production_date: fmt(d.production_date),
          expiry_date: fmt(d.expiry_date),
        })),
      };

      if (isCreate) {
        const res = await pkService.create(payload);
        const newCode = res.data?.data?.code;
        message.success(`บันทึกสำเร็จ ${newCode || ""}`);
      } else {
        await pkService.update(payload);
        message.success("อัพเดทสำเร็จ");
      }
      navigate(-1);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const code = form.getFieldValue("pk_code");
    if (!code) return;
    window.open(`/packing-list-print/${code}`, "_blank");
  };

  const columns = detailColumns({ updateDetail, deleteDetail, readOnly: isView });

  return (
    <div className="packing-list-manage">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>

        {/* Toolbar */}
        <Flex justify="space-between" align="center">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config?.title || "Packing List"}
          </Typography.Title>
          <Space wrap>
            <Button size="small" className="bn-action" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              ย้อนกลับ
            </Button>
            <ActionGroup type="print">
              {!isCreate && <PrintButton onClick={handlePrint}>พิมพ์ Packing List</PrintButton>}
            </ActionGroup>
            {!isView && (
              <Button
                type="primary" size="small" className="bn-action"
                icon={<SaveOutlined />} loading={saving} onClick={handleSave}
              >
                บันทึก
              </Button>
            )}
          </Space>
        </Flex>

        <Form form={form} layout="vertical" autoComplete="off" disabled={isView}>

          {/* ── ข้อมูลเอกสาร ── */}
          <Card title="ข้อมูลเอกสาร" loading={loading} style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              {!isCreate && (
                <Col xs={24} sm={8} md={4}>
                  <Form.Item label="เลขที่ Packing List" name="pk_code">
                    <Input disabled />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={8} md={5}>
                <Form.Item
                  label="เลขที่ใบส่งมอบ"
                  name="delcode"
                  rules={[{ required: true, message: "กรุณาเลือกใบส่งมอบ" }]}
                >
                  <Input
                    readOnly
                    placeholder="เลือกใบส่งมอบ"
                    onClick={openDeliveryPicker}
                    addonAfter={
                      <SearchOutlined
                        style={{ color: "#1677ff", cursor: "pointer" }}
                        onClick={openDeliveryPicker}
                      />
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Form.Item label="Job Order" name="jocode">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={5}>
                <Form.Item
                  label="วันที่เอกสาร"
                  name="pk_date"
                  rules={[{ required: true, message: "กรุณาเลือกวันที่" }]}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Form.Item label="ลูกค้า" name="cusname">
                  <Input disabled />
                </Form.Item>
              </Col>
              {/* hidden */}
              <Form.Item name="cuscode" hidden><Input /></Form.Item>
              <Form.Item name="brand" hidden><Input /></Form.Item>
            </Row>
          </Card>

          {/* ── TO / CONSIGNEE ── */}
          <Card title="ผู้รับสินค้า (TO / CONSIGNEE & NOTIFY PARTY)" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={8}>
                <Form.Item label="ชื่อผู้รับสินค้า" name="consignee_name">
                  <Input placeholder="เช่น FINE FOODS OY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item label="ที่อยู่" name="consignee_address">
                  <TextArea rows={2} placeholder="ที่อยู่ผู้รับสินค้า" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="EORI #" name="eori_no">
                  <Input placeholder="เช่น FI0660128-8" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="CONSIGNEE & NOTIFY PARTY" name="notify_party">
                  <Input placeholder="SAME AS ABOVE" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ── Invoice + เงื่อนไขชำระเงิน ── */}
          <Card title="ข้อมูลใบกำกับ / เงื่อนไขชำระเงิน" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} md={5}>
                <Form.Item label="No. of invoice" name="inv_no">
                  <Input placeholder="เช่น NSF24014" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={5}>
                <Form.Item label="Date of invoice" name="inv_date">
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={5}>
                <Form.Item label="Customer PO" name="customer_po">
                  <Input placeholder="เช่น 12015557" />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item label="Term of Payment" name="payment_term">
                  <Input placeholder="เช่น 100 % BY T/T 30 DAYS AFTER BL DATE" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ── ข้อมูลขนส่ง ── */}
          <Card title="ข้อมูลขนส่ง" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Place of receipt" name="place_of_receipt">
                  <Input placeholder="เช่น LAEM CHABANG,THAILAND" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Final destination" name="final_destination">
                  <Input placeholder="เช่น HELSINKI, FINLAND" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item label="ETD" name="etd">
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item label="ETA" name="eta">
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label="Country of origin" name="country_of_origin">
                  <Input placeholder="THAILAND" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Carrier"
                  name="carrier"
                  tooltip="ใส่ได้หลายลำ กด Enter ขึ้นบรรทัดใหม่"
                >
                  <TextArea rows={2} placeholder={"CAPE FELTON V.897S\nONE HONG KONG V.081W"} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ── Advising Bank ── */}
          <Card title="Advising Bank" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} md={4}>
                <Form.Item label="SWIFT" name="bank_swift">
                  <Input placeholder="เช่น BKKBTHBK" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Form.Item label="ACC NAME" name="bank_acc_name">
                  <Input placeholder="NINE STAR FOOD CO.,LTD" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Form.Item label="ACC NO." name="bank_acc_no">
                  <Input placeholder="181-499903-1" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item label="สาขา" name="bank_branch">
                  <Input placeholder="LADPRAO 99 BRANCH" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item label="ชื่อธนาคาร" name="bank_name">
                  <Input placeholder="BANGKOK BANK PUBLIC CO.,LTD" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* ── รายการสินค้า ── */}
          <Card
            title="รายการสินค้า"
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Typography.Text type="secondary">
                  รวม {totals.cases.toLocaleString()} CASE / NW {totals.net.toLocaleString()} / GW {totals.gross.toLocaleString()}
                </Typography.Text>
                <Button size="small" onClick={applyTotals}>ใช้ยอดรวมนี้</Button>
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="seq"
              columns={columns}
              dataSource={detail}
              pagination={false}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: "เลือกใบส่งมอบเพื่อดึงรายการสินค้า" }}
            />
          </Card>

          {/* ── สรุปท้ายเอกสาร ── */}
          <Card title="สรุปท้ายเอกสาร">
            <Row gutter={[16, 0]}>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="LOADING (จำนวน)" name="loading_qty">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item
                  label="หน่วย"
                  name="loading_unit"
                  tooltip="ค่าตั้งต้นมาจากประเภทหน่วยบรรจุของสินค้าในใบส่งมอบ (er_pg.pack_type) แก้ทับได้"
                  /* สินค้าในใบเดียวกันบรรจุคนละแบบ ต้องเตือนให้คนตรวจก่อนพิมพ์
                     เพราะใบ Packing List มีหน่วยการโหลดได้หน่วยเดียว */
                  extra={
                    packTypeMixed ? (
                      <Typography.Text type="warning" style={{ fontSize: 12 }}>
                        ใบนี้มีทั้งกล่องและพาเลต โปรดตรวจหน่วยก่อนพิมพ์
                      </Typography.Text>
                    ) : null
                  }
                >
                  <Select options={LOADING_UNIT_OPTIONS} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="NET WEIGHT (KG)" name="net_weight">
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="GROSS WEIGHT (KG)" name="gross_weight">
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="VOLUME (CBM)" name="volume_cbm">
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "4px 0 16px" }} />

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="ชื่อผู้ลงนาม" name="signer_name">
                  <Input placeholder="เช่น MS PANIPA HANVIVATANAKIT" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="ตำแหน่งผู้ลงนาม" name="signer_position">
                  <Input placeholder="เช่น MARKETING DIRECTOR" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="หมายเหตุ" name="remark">
                  <Input placeholder="หมายเหตุภายใน" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Space>

      {/* Modal เลือกใบส่งมอบ */}
      <Modal
        title="เลือกใบส่งมอบ (Delivery Note)"
        open={openDelModal}
        onCancel={() => setOpenDelModal(false)}
        footer={null}
        width={1000}
      >
        <Flex gap={8} style={{ marginBottom: 12 }}>
          <Input
            placeholder="ค้นหา เลขที่ใบส่งมอบ / JO / ลูกค้า / brand"
            value={delKeyword}
            allowClear
            onChange={(e) => setDelKeyword(e.target.value)}
            onPressEnter={() => loadDeliveryList(delKeyword, showAllDel)}
          />
          <Button icon={<SearchOutlined />} onClick={() => loadDeliveryList(delKeyword, showAllDel)}>
            ค้นหา
          </Button>
          <Button
            type={showAllDel ? "primary" : "default"}
            onClick={() => {
              const next = !showAllDel;
              setShowAllDel(next);
              loadDeliveryList(delKeyword, next);
            }}
          >
            {showAllDel ? "แสดงทั้งหมด" : "ซ่อนใบที่ทำแล้ว"}
          </Button>
        </Flex>
        <Table
          size="small"
          rowKey="delcode"
          loading={delLoading}
          columns={deliveryColumn()}
          dataSource={delList}
          scroll={{ x: "max-content", y: 400 }}
          pagination={{ pageSize: 10 }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => handleSelectDelivery(record),
          })}
        />
      </Modal>
    </div>
  );
};

export default MyManage;
